import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCookie,
  faCheck,
  faXmark,
  faGear,
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../Context/Language';
import './CookieConsent.css';

const CONSENT_KEY = 'ferramentum_cookie_consent';
const SETTINGS_KEY = 'ferramentum_cookie_settings';

function getDefaultSettings() {
  return {
    necessary: true,
    functional: false,
    analytical: false,
    marketing: false,
  };
}

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function readStoredSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function saveConsent(consent) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {}
}

function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}

function ConsentBanner({ onAcceptAll, onRejectAll, onOpenSettings }) {
  const { t } = useLanguage();

  return (
    <div className="cc-banner" role="dialog" aria-labelledby="cc-banner-title" aria-modal="true">
      <div className="cc-banner-inner">
        <div className="cc-banner-icon">
          <FontAwesomeIcon icon={faCookie} />
        </div>
        <div className="cc-banner-text">
          <h2 id="cc-banner-title">{t('We value your privacy')}</h2>
          <p>
            {t('We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of all cookies. You can manage your preferences or learn more by clicking "Cookie Settings".')}
          </p>
        </div>
      </div>
      <div className="cc-banner-actions">
        <button type="button" className="cc-btn cc-btn-text" onClick={onOpenSettings}>
          <FontAwesomeIcon icon={faGear} /> {t('Cookie Settings')}
        </button>
        <button type="button" className="cc-btn cc-btn-outline" onClick={onRejectAll}>
          {t('Reject All')}
        </button>
        <button type="button" className="cc-btn cc-btn-primary" onClick={onAcceptAll}>
          <FontAwesomeIcon icon={faCheck} /> {t('Accept All')}
        </button>
      </div>
    </div>
  );
}

function ConsentSettings({ initialSettings, onSave, onClose }) {
  const { t } = useLanguage();

  const categories = [
    {
      id: 'necessary',
      label: t('Strictly Necessary'),
      description: t('Essential cookies required for the Site to function — authentication, cart, security. Cannot be disabled.'),
      required: true,
    },
    {
      id: 'functional',
      label: t('Functional'),
      description: t('Remember your preferences (language, region) for a personalized experience.'),
      required: false,
    },
    {
      id: 'analytical',
      label: t('Analytical'),
      description: t('Help us understand how visitors use the Site so we can improve it. All data is anonymized.'),
      required: false,
    },
    {
      id: 'marketing',
      label: t('Marketing'),
      description: t('Used to deliver relevant advertisements and measure campaign effectiveness.'),
      required: false,
    },
  ];

  const [settings, setSettings] = useState(() => {
    const stored = readStoredSettings();
    if (stored) return stored;
    const defaults = getDefaultSettings();
    if (initialSettings) {
      Object.assign(defaults, initialSettings);
    }
    return defaults;
  });

  const toggle = (id) => {
    if (id === 'necessary') return;
    setSettings((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    saveSettings(settings);
    onSave(settings);
  };

  return (
    <div className="cc-overlay" onClick={onClose}>
      <div
        className="cc-settings-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="cc-settings-title"
        aria-modal="true"
      >
        <div className="cc-settings-header">
          <h2 id="cc-settings-title">{t('Cookie Preferences')}</h2>
          <button type="button" className="cc-close-btn" onClick={onClose} aria-label={t('Close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <p className="cc-settings-intro">
          {t('We use cookies and similar technologies to ensure the Site functions correctly, enhance your experience, analyze usage, and deliver relevant content. You can choose which categories to accept below.')}
        </p>

        <div className="cc-category-list">
          {categories.map((cat) => (
            <div key={cat.id} className="cc-category-item">
              <div className="cc-category-info">
                <span className="cc-category-label">{cat.label}</span>
                <span className="cc-category-desc">{cat.description}</span>
              </div>
              <button
                type="button"
                className={`cc-toggle ${settings[cat.id] ? 'cc-toggle-on' : 'cc-toggle-off'}`}
                onClick={() => toggle(cat.id)}
                disabled={cat.required}
                aria-pressed={settings[cat.id]}
                aria-label={`${cat.label} cookies`}
                title={cat.required ? t('Always required') : t('Toggle {category} cookies').replace('{category}', cat.label)}
              >
                <span className="cc-toggle-knob" />
              </button>
            </div>
          ))}
        </div>

        <div className="cc-settings-actions">
          <button type="button" className="cc-btn cc-btn-primary" onClick={handleSave}>
            {t('Save Preferences')}
          </button>
          <button type="button" className="cc-btn cc-btn-text" onClick={() => onSave(getDefaultSettings())}>
            {t('Accept Only Necessary')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const existing = readStoredConsent();
    if (!existing) {
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytical: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    saveConsent(allAccepted);
    saveSettings(allAccepted);
    setIsVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytical: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    saveConsent(onlyNecessary);
    saveSettings(onlyNecessary);
    setIsVisible(false);
  }, []);

  const handleSaveSettings = useCallback((settings) => {
    const consent = {
      ...settings,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    saveConsent(consent);
    saveSettings(settings);
    setIsVisible(false);
    setShowSettings(false);
    setShowBanner(false);
  }, []);

  const handleOpenSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  if (!mounted) return null;

  const existing = readStoredConsent();

  return createPortal(
    <>
      {isVisible && !showSettings && showBanner && (
        <ConsentBanner
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onOpenSettings={handleOpenSettings}
        />
      )}

      {showSettings && (
        <ConsentSettings
          initialSettings={existing || undefined}
          onSave={handleSaveSettings}
          onClose={handleCloseSettings}
        />
      )}
    </>,
    document.body
  );
}

export default CookieConsent;